import React, { useLayoutEffect, useRef } from 'react';
import SystemStats from './SystemStats';

// The <dialog-box> intrinsic element is declared in JobLogDialog.tsx; interface
// merging makes it available across the program, so it is not repeated here.

/** The parts of DialogBox we drive imperatively. */
type DialogBoxElement = HTMLElement & {
  show(): void;
  close(): void;
  autoKill: boolean;
  onCloseHandler: (() => void) | null;
};

interface SystemStatsDialogProps {
  serverId: number;
  pollInterval?: number;
  onClose: () => void;
}

/**
 * The system stats in a modal. Closing is always driven by the element itself.
 *
 * The three constraints below are not optional when driving <dialog-box> from
 * React - see web/js/admin/README.md for the full write-up.
 */
export default function SystemStatsDialog({
  serverId,
  pollInterval,
  onClose,
}: SystemStatsDialogProps) {
  const dialogRef = useRef<HTMLElement | null>(null);

  // Kept in a ref so the effect below can stay mount-only without going stale.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // useLayoutEffect, not useEffect: autoKill and onCloseHandler have to be in
  // place before anything can fire, and show() before paint so there's no flash.
  useLayoutEffect(() => {
    const dialog = dialogRef.current as DialogBoxElement | null;

    if (!dialog) return;

    // DialogBox removes its own host element on close. React owns this node, so
    // letting the element delete itself leaves React removing a node that has
    // already gone.
    dialog.autoKill = false;

    // A property with a setter, not an attribute, so it can't come from JSX.
    // Both close paths - the title-bar X and Escape - funnel through here.
    dialog.onCloseHandler = () => onCloseRef.current();

    dialog.show();

    return () => {
      // DialogBox adds its Escape listener to `window` and never removes it, so
      // a discarded element can still be closed. Cut the link back to React
      // before we let go of it.
      dialog.onCloseHandler = null;
    };
  }, []);

  return (
    <dialog-box
      ref={(element) => {
        dialogRef.current = element;
      }}
      dialog-title="System Stats"
      width="900"
      height="600"
      // Read once in connectedCallback, so it has to be an attribute set before
      // insertion - a ref effect would run too late. Stops .dialog-content from
      // scrolling as well, leaving the tab panel as the only scroller.
      noscroll="noscroll"
    >
      <div slot="content" className="system-stats-dialog__content">
        <SystemStats serverId={serverId} pollInterval={pollInterval} />
      </div>
    </dialog-box>
  );
}
